import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  Alert,
  Tooltip,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EmojiPicker from './EmojiPicker';

const DEFAULT_STATE = {
  name: '',
  description: '',
  icon: '✨',
  prompt_template:
    'Erstelle eine strukturierte Auswertung des folgenden Transkripts.\n\nTranskript:\n{transcript}',
};

const PLACEHOLDERS = [
  {
    token: '{transcript}',
    label: 'Transkript einfügen',
    tooltip:
      'Fügt das Transkript an dieser Stelle ein. Längere Transkripte werden abschnittsweise bearbeitet und die Ergebnisse am Ende zu einem Gesamtergebnis zusammengeführt.',
    icon: <ArticleIcon fontSize="small" />,
  },
];

export default function OneClickForm({ open, initial, onClose, onSubmit }) {
  const [state, setState] = useState(DEFAULT_STATE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const textRef = useRef(null);

  useEffect(() => {
    if (open) {
      setState(initial || DEFAULT_STATE);
      setError(null);
    }
  }, [open, initial]);

  const change = (key) => (e) =>
    setState((s) => ({ ...s, [key]: e.target.value }));

  const setIcon = (emoji) => setState((s) => ({ ...s, icon: emoji }));

  const insertPlaceholder = useCallback((token) => {
    setState((s) => {
      const current = s.prompt_template || '';
      if (current.includes(token)) return s;

      const el = textRef.current;
      let cursor = current.length;
      if (el) {
        cursor = el.selectionStart ?? current.length;
      }
      const before = current.slice(0, cursor);
      const after = current.slice(cursor);
      const needsNewlineBefore = before.length > 0 && !before.endsWith('\n');
      const insert = `${needsNewlineBefore ? '\n' : ''}${token}`;
      const next = `${before}${insert}${after}`;

      setTimeout(() => {
        if (!textRef.current) return;
        const pos = before.length + insert.length;
        textRef.current.focus();
        try {
          textRef.current.setSelectionRange(pos, pos);
        } catch {
          /* ignore */
        }
      }, 0);

      return { ...s, prompt_template: next };
    });
  }, []);

  const canSave =
    state.name?.trim() && state.prompt_template?.trim() && !saving;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        name: state.name.trim(),
        description: state.description?.trim() || '',
        icon: (state.icon || '✨').trim() || '✨',
        prompt_template: state.prompt_template,
      });
      onClose();
    } catch (e) {
      setError(e.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initial?.id ? 'Funktion bearbeiten' : 'Neue 1-Click KI-Funktion'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box display="flex" gap={2} alignItems="flex-start">
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5, display: 'block', mb: 0.5 }}>
                Icon
              </Typography>
              <EmojiPicker value={state.icon} onChange={setIcon} />
            </Box>
            <TextField
              label="Name"
              value={state.name}
              onChange={change('name')}
              fullWidth
              autoFocus
              required
              sx={{ mt: 2.5 }}
            />
          </Box>

          <TextField
            label="Kurzbeschreibung (optional)"
            value={state.description}
            onChange={change('description')}
            fullWidth
          />

          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
              <Typography variant="body2" fontWeight={500}>
                Prompt-Template
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
                {PLACEHOLDERS.map((ph) => {
                  const already = (state.prompt_template || '').includes(ph.token);
                  return (
                    <Tooltip
                      key={ph.token}
                      title={already ? `${ph.token} ist bereits eingefügt` : ph.tooltip}
                    >
                      <span>
                        <Button
                          size="small"
                          variant={already ? 'outlined' : 'contained'}
                          color={already ? 'success' : 'primary'}
                          startIcon={ph.icon}
                          disabled={already}
                          onClick={() => insertPlaceholder(ph.token)}
                        >
                          {already ? `${ph.token} ✓` : ph.label}
                        </Button>
                      </span>
                    </Tooltip>
                  );
                })}
              </Stack>
            </Stack>
            <TextField
              inputRef={textRef}
              value={state.prompt_template}
              onChange={change('prompt_template')}
              fullWidth
              multiline
              minRows={10}
              maxRows={24}
              required
              placeholder="Beschreibe die Aufgabe und füge mit dem Button oben den Platzhalter für das Transkript ein."
            />
            <Stack direction="row" alignItems="flex-start" spacing={0.75} mt={0.5}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', mt: '2px' }} />
              <Typography variant="caption" color="text.secondary">
                Beim Ausführen entscheidet das System automatisch: passt das Transkript
                ins Kontextfenster, wird es vollständig übergeben — ist es zu lang,
                werden stattdessen die thematisch relevantesten Ausschnitte per RAG
                eingefügt. Im Ergebnis wird der verwendete Modus angezeigt.
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Abbrechen</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {initial?.id ? 'Speichern' : 'Anlegen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
